import React, { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Typography, Tabs, Tab, useTheme } from '@mui/material';
import { Block } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../hooks/useModulePermissions';
import finePaymentService from '../services/finePaymentService';
import { FinePaymentsSummary } from '../types/letters';
import FinesKpiStrip from '../components/fines/FinesKpiStrip';
import OutstandingFinesTable from '../components/fines/OutstandingFinesTable';
import CollectedFinesTable from '../components/fines/CollectedFinesTable';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';


type TabKey = 'outstanding' | 'collected';

const FinesPage: React.FC = () => {
  const fmt = useCurrencyFormat();
  const theme = useTheme();
  const { canRead } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState<TabKey>('outstanding');
  const [summary, setSummary] = useState<FinePaymentsSummary | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadSummary = useCallback(async () => {
    try {
      const s = await finePaymentService.getFinePaymentsSummary();
      setSummary(s);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to load fines summary', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const triggerRefresh = () => {
    setRefreshKey(k => k + 1);
    loadSummary();
  };

  if (!canRead('fines')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', maxWidth: 420, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Block sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={500}>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don't have permission to view Fines.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const collectedKpis = [
    { label: 'Collected today', value: fmt(summary?.collectedToday ?? 0), color: theme.palette.success.main, caption: `${summary?.collectedTodayCount ?? 0} payments` },
    { label: 'Collected this month', value: fmt(summary?.collectedThisMonth ?? 0), color: theme.palette.success.dark, caption: `${summary?.collectedThisMonthCount ?? 0} payments` },
    { label: 'Collected this year', value: fmt(summary?.collectedThisYear ?? 0), color: theme.palette.info.main, caption: `${summary?.collectedThisYearCount ?? 0} payments` },
    { label: 'Outstanding', value: fmt(summary?.outstandingTotal ?? 0), color: theme.palette.error.main, caption: `${summary?.outstandingCount ?? 0} fines` },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Fines</Typography>
        <Typography variant="body2" color="text.secondary">
          Track outstanding student fines and the ledger of collected payments.
        </Typography>
      </Box>

      {/* KPIs */}
      <Box sx={{ mb: 3 }}>
        <FinesKpiStrip items={collectedKpis} />
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 2, overflow: 'hidden',
      }}>
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setTab(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minHeight: 48 },
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Tab value="outstanding" label={`Outstanding${summary?.outstandingCount ? ` (${summary.outstandingCount})` : ''}`} />
          <Tab value="collected" label="Collected" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 'outstanding' && <OutstandingFinesTable refreshKey={refreshKey} onChanged={triggerRefresh} />}
          {tab === 'collected' && <CollectedFinesTable refreshKey={refreshKey} onChanged={triggerRefresh} />}
        </Box>
      </Paper>
    </Box>
  );
};

export default FinesPage;
